import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { TREND_CHART_SERIES_COLORS, TrendChart } from '../../../media/breakdown/TrendChart.js';
import { ErrorState } from '../../../media/shared/ErrorState.js';
import { NoData } from '../../../media/shared/NoData.js';
import { bucketLabel, formatTrendValue } from '../../data/format.js';
import { type CreativeDetailScope, type DeliveryNode, useCreativeTrend } from '../data/api.js';
import { SectionSkeleton } from '../shared/SectionSkeleton.js';

const MAX_TICKS = 7;

interface CreativeTrendChartProps {
  scope: CreativeDetailScope;
  creativeId: string;
  node: DeliveryNode | undefined;
  metricNames: [string] | [string, string];
  legendAlign?: 'left' | 'center';
}

export function CreativeTrendChart({
  scope,
  creativeId,
  node,
  metricNames,
  legendAlign = 'left',
}: CreativeTrendChartProps): ReactNode {
  const theme = useTheme();
  const trend = useCreativeTrend(
    {
      ...scope,
      creativeId,
      ...(node === undefined ? {} : { node }),
      metrics: metricNames.map((name) => ({ name, aggregation: 'total' as const })),
    },
    true,
  );

  if (trend.isError) return <ErrorState />;
  if (trend.data === undefined) return <SectionSkeleton variant="chart" />;

  const { metrics, granularity, currencySymbol } = trend.data;
  if (metrics.length === 0) return <NoData message="No trend data for the selected metrics." />;

  const buckets = metrics[0]?.current.series.map((point) => point.bucket) ?? [];
  const tickStep = Math.max(1, Math.ceil(buckets.length / MAX_TICKS));
  const dateForBucket = (bucket: number): string | undefined =>
    metrics[0]?.current.series[bucket - 1]?.date;

  return (
    <>
      <Box>
        <TrendChart
          xAxisData={buckets}
          xAxisTickFormatter={(bucket) => {
            const labelled = (Number(bucket) - 1) % tickStep === 0 || bucket === buckets.length;
            if (!labelled) return '';
            const date = dateForBucket(Number(bucket));
            return date === undefined ? '' : bucketLabel(date, granularity);
          }}
          xAxisValueFormatter={(bucket) => {
            const date = dateForBucket(Number(bucket));
            return date === undefined ? `#${bucket}` : bucketLabel(date, granularity);
          }}
          series={metrics.map((metric) => ({
            id: metric.name,
            label: metric.label,
            values: metric.current.series.map((point) => point.value),
            valueFormatter: (value: number) =>
              formatTrendValue(metric.name, value, metric.format, currencySymbol),
          }))}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: legendAlign === 'center' ? 'center' : 'flex-start',
          gap: 4,
          mt: 2,
          pl: legendAlign === 'center' ? 0 : 5,
        }}
      >
        {metrics.map((metric, index) => (
          <Box key={metric.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: TREND_CHART_SERIES_COLORS[index] ?? theme.palette.text.disabled,
              }}
            />
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>
              {metric.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  );
}
